import { createFileRoute, notFound } from "@tanstack/react-router";
import { HelpArticlePage } from "@/components/marketing/help-article-page";
import { NotFoundPage } from "@/components/marketing/not-found-page";
import {
  createPageHead,
  helpArticleJsonLd,
  helpBreadcrumbJsonLd,
} from "@/lib/seo";

/**
 * The article comes from the loader, not a module-scope import.
 *
 * `head` is not code-split — it has to run during SSR and again on a client
 * navigation — so importing the help corpus here put all ~148 KiB of it in the
 * app's ENTRY chunk, and every visitor to the landing page downloaded the whole
 * help centre before the page could paint. Loading it inside the loader makes
 * the import dynamic: it now ships only to the routes that render it.
 */
export const Route = createFileRoute("/(public)/help/$slug")({
  loader: async ({ params }) => {
    const { getHelpArticle } = await import("@/components/marketing/help-data");
    const article = getHelpArticle(params.slug);

    // Throwing rather than rendering the not-found page at 200: an unknown
    // slug answered OK, which Search Console reports as a soft 404 and which
    // spends a crawl on a URL that does not exist.
    if (!article) {
      throw notFound();
    }

    return { article };
  },
  head: ({ loaderData }) => {
    const article = loaderData?.article;

    // `notFound()` in the loader means `head` still runs with no data on the
    // way to the not-found component; the root's own head covers that case.
    if (!article) {
      return {};
    }

    return createPageHead(
      {
        path: `/help/${article.slug}`,
        title: `${article.title} — PostDaily Help`,
        description: article.excerpt,
        ogType: "article",
      },
      { jsonLd: [helpArticleJsonLd(article), helpBreadcrumbJsonLd(article)] },
    );
  },
  notFoundComponent: NotFoundPage,
  component: HelpArticleRoute,
});

function HelpArticleRoute() {
  const { article } = Route.useLoaderData();

  return <HelpArticlePage article={article} />;
}
